/**
 * WikiPageAnalyzer — 채팅 쓰레드/메시지를 위키 페이지 형식으로 변환
 *
 * TaskAnalyzer 패턴 참고.
 * Firebase AI Logic SDK (Gemini 2.5 Flash-Lite) 사용.
 *
 * 출력: 위키 페이지에 잘 어울리는 구조화된 형식.
 *   - title: 짧은 제목 (한 줄)
 *   - summary: 1-2문장 개요 (페이지 상단 소개)
 *   - sections: [{ heading, bullets[] }] 본문 (소제목 + 요점)
 *   - tags: 검색용 태그
 *
 * 클라이언트에서 TipTap JSON 으로 변환하여 모달 prefill.
 */

import { getAiTextModel } from './AiTextClient';
import { getApp } from 'firebase/app';

const MODEL_NAME = 'gemini-2.5-flash-lite';

// 서버 경유 — 브라우저가 Firebase AI Logic 을 직접 부르면 공개된 클라이언트 설정만으로
// 외부인이 우리 프로젝트 명의로 모델을 호출할 수 있다 (2026-08-10 비용 사고). 계약은 동일.
function getModel() {
  return getAiTextModel(MODEL_NAME);
}

function parseJSON(text: string): any {
  const cleaned = text
    .replace(/^```json?\n?/g, '')
    .replace(/\n?```$/g, '')
    .trim();
  return JSON.parse(cleaned);
}

function langLabel(lang: string): string {
  if (lang.startsWith('ko')) return 'Korean';
  if (lang.startsWith('ja')) return 'Japanese';
  return 'English';
}

export interface WikiPageSection {
  heading: string;
  bullets: string[];
}

export interface WikiPageAnalysisResult {
  title: string;
  summary: string;
  sections: WikiPageSection[];
  tags: string[];
}

/**
 * 쓰레드/메시지 텍스트를 AI로 분석하여 위키 페이지에 어울리는 구조화된 형식을 추출한다.
 *
 * @param threadText - 쓰레드 또는 단일 메시지 텍스트
 * @param language - 사용자 언어 (ko, ja, en)
 */
export async function analyzeChatForWikiPage(
  threadText: string,
  language?: string,
): Promise<WikiPageAnalysisResult> {
  const lang = language || 'ko';
  const model = getModel();

  const prompt = `You are an AI assistant that converts chat conversations into well-structured wiki pages for a team knowledge base.

**Chat conversation:**
${threadText}

**Goal:**
Distill the conversation into a clean, reusable wiki page. Strip out chitchat, focus on facts/decisions/how-to/conclusions that future team members would want to look up.

**Instructions:**
1. Produce a short, descriptive title (one line).
2. Write a 1-2 sentence summary that opens the page (overview of what this page covers).
3. Organize the remaining content into 2-5 sections. Each section has a heading and 2-6 bullets.
   - Prefer sections like: 배경 / 결정사항 / 핵심 요점 / 절차 / 결과 / 참고 — but adapt to what the chat actually contains.
   - Bullets should be self-contained statements, not raw chat fragments. Reword for clarity.
4. Generate 3-5 tags suitable for search.

**Response language:** ${langLabel(lang)}

**IMPORTANT:** Return ONLY valid JSON, no markdown code blocks. Use this exact structure:
{
  "title": "wiki page title",
  "summary": "one or two sentence overview",
  "sections": [
    { "heading": "section heading", "bullets": ["point 1", "point 2"] }
  ],
  "tags": ["tag1", "tag2", "tag3"]
}`;

  const result = await model.generateContent(prompt);
  const text = result.response.text();
  const parsed = parseJSON(text);

  return {
    title: parsed.title || '',
    summary: parsed.summary || '',
    sections: Array.isArray(parsed.sections)
      ? parsed.sections.map((s: any) => ({
          heading: s.heading || '',
          bullets: Array.isArray(s.bullets) ? s.bullets : [],
        })).filter((s: WikiPageSection) => s.heading && s.bullets.length > 0)
      : [],
    tags: Array.isArray(parsed.tags) ? parsed.tags.slice(0, 5) : [],
  };
}

/**
 * 분석 결과를 TipTap JSON 문서로 변환한다.
 * 본문 구조:
 *   - paragraph(summary)
 *   - heading(level=2) + bulletList(items) ... 반복
 */
export function buildWikiPageTipTapJson(result: WikiPageAnalysisResult): any {
  const content: any[] = [];

  if (result.summary) {
    content.push({
      type: 'paragraph',
      content: [{ type: 'text', text: result.summary }],
    });
  }

  for (const section of result.sections) {
    content.push({
      type: 'heading',
      attrs: { level: 2 },
      content: [{ type: 'text', text: section.heading }],
    });
    content.push({
      type: 'bulletList',
      content: section.bullets.map(b => ({
        type: 'listItem',
        content: [{
          type: 'paragraph',
          content: [{ type: 'text', text: b }],
        }],
      })),
    });
  }

  return {
    type: 'doc',
    content: content.length > 0 ? content : [{ type: 'paragraph' }],
  };
}
